import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Pagination,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Getusers } from "../Redux/Actions/AuthAction";
import { FiTrash } from "react-icons/fi";
import DeleteAccount from "../Models/DeleteAccount";
import Loader from "../components/Loader/Loading";
import notfound from "../assets/images/not-found.png";
import SearchField from "../context/SearchField";
import useTitle from "../hooks/useTitle";

const ManageUsers = () => {
  useTitle("Users");
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [cachedPages, setCachedPages] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { users, loading } = useSelector((state) => state.Auth);
  const [search, setSearch] = useState("");
  const [openDeletemodal, setopendeletemodal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setCachedPages({});
  }, [debouncedSearch]);

  useEffect(() => {
    const scrollToTop = () => {
      const mainContent = document.getElementById("main-content");
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    if (!cachedPages[page]) {
      dispatch(Getusers(page, debouncedSearch, rowsPerPage)).then((res) => {
        setCachedPages((prev) => ({
          ...prev,
          [page]: res?.data,
        }));
        scrollToTop();
      });
    }
  }, [dispatch, page, debouncedSearch, rowsPerPage]);

  const handledelete = (user) => {
    setSelectedUser(user);
    setopendeletemodal(true);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            mb: 2,
            gap: 1,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              color: "#424242",
              fontSize: { xs: "1rem", sm: "1.3rem" },
            }}
          >
            Manage Users
          </Typography>
          <SearchField search={search} setSearch={setSearch} />
        </Box>
        
        <div className="w-full overflow-x-auto z-[0]">
          <Paper
            sx={{
              height: "auto",
              backgroundColor: "transparent",
            }}
          >
            <TableContainer sx={{ minHeight: "78vh" }} className="relative">
              <Table aria-label="sticky table">
                <TableHead sx={{ backgroundColor: "transparent" }}>
                  <TableRow className="!font-bold">
                    <TableCell
                      align="left"
                      className="!font-semibold"
                      sx={{ padding: "12px" }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      align="left"
                      className="!font-semibold"
                      sx={{ padding: "12px" }}
                    >
                      Country
                    </TableCell>
                    <TableCell
                      align="left"
                      className="!font-semibold"
                      sx={{ padding: "12px" }}
                    >
                      Phone
                    </TableCell>
                    <TableCell
                      align="center"
                      className="!font-semibold"
                      sx={{ padding: "12px" }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <Loader />
                  ) : (
                    users?.data?.map((obj) => (
                      <TableRow key={obj.id}>
                        <TableCell align="left" sx={{ padding: "12px" }}>
                          <div className="flex items-center gap-2">
                            <Avatar src={obj?.profileImage} />
                            <span className="text-nowrap">
                              {obj?.first_name} {obj?.last_name}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell align="left" sx={{ padding: "12px" }}>
                          <span> {obj?.country}</span>
                        </TableCell>

                        <TableCell align="left" sx={{ padding: "12px" }}>
                          <span className="text-nowrap">
                            +{obj?.code} {obj?.phone}
                          </span>
                        </TableCell>

                        <TableCell align="center" sx={{ padding: "12px" }}>
                          <IconButton
                            sx={{ color: "#F1580C" }}
                            onClick={() => handledelete(obj)}
                            color="error"
                            size="small"
                          >
                            <FiTrash />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {!loading && users?.data?.length === 0 && (
                <div className="flex flex-col justify-center items-center w-full h-[60vh]">
                  <img
                    src={notfound}
                    alt="Not Found"
                    style={{ width: "200px", height: "auto" }}
                  />
                  <span className="text-xl font-bold mt-6">
                    No data found
                  </span>
                </div>
              )}
            </TableContainer>

            <div className="flex justify-end pt-3 ">
              <Pagination
                shape="rounded"
                count={Math.ceil(users?.totalCount / rowsPerPage)}
                page={page}
                onChange={handlePageChange}
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    background:
                      "linear-gradient(to right, #EF5A24 1%, #F38157 99%)",
                    color: "#fff",
                  },

                  "@media (max-width: 960px)": {
                    fontSize: "0.75rem",
                    "& .MuiPaginationItem-root": {
                      minWidth: "auto",
                      padding: "8px",
                      margin: "2px",
                    },
                  },
                  "@media (max-width: 600px)": {
                    fontSize: "0.5rem",
                    "& .MuiPaginationItem-root": {
                      padding: "8px",
                      margin: "1px",
                    },
                  },
                }}
              />
            </div>
          </Paper>
        </div>
      </Box>
      <DeleteAccount
        Open={openDeletemodal}
        setOpen={setopendeletemodal}
        user={selectedUser}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        cachedPages={cachedPages}
        setCachedPages={setCachedPages}
        debouncedSearch={debouncedSearch}
        setDebouncedSearch={setDebouncedSearch}
      />
    </>
  );
};

export default ManageUsers;
